import { useMemo } from "react";
import { Box, Typography, Button, Autocomplete, TextField, Card, CardContent, Grid } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WifiIcon from "@mui/icons-material/Wifi";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { StatCard } from "../components/StatCard";
import { SortableTable } from "../components/SortableTable";
import { baseChartOptions } from "../components/chartBase";
import { useData } from "../context/DataContext";

interface ShipAnalysisProps {
  selectedVessel: string | null;
  onSelectVessel: (vessel: string | null) => void;
  onBackToFleet: () => void;
}

export default function ShipAnalysis({ selectedVessel, onSelectVessel, onBackToFleet }: ShipAnalysisProps) {
  const dashboardData = useData();
  const { siteAggregates, rawData } = dashboardData;

  const vesselData = useMemo(() => {
    if (!selectedVessel) return [];
    return rawData.filter(d => d.site === selectedVessel).sort((a, b) => a.date.localeCompare(b.date));
  }, [selectedVessel, rawData]);

  const vesselAgg = useMemo(() => {
    if (!selectedVessel) return null;
    return siteAggregates.find(s => s.site === selectedVessel);
  }, [selectedVessel, siteAggregates]);

  const aggregateColumns = [
    { key: "site", header: "Vessel Name", sortable: true, sortFn: (a: any, b: any) => a.site.localeCompare(b.site), cell: (row: any) => <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "primary.main", textDecoration: "underline" }}>{row.site}</Box> },
    { key: "totalWan", header: "Total WAN", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => (a.totalWan ?? 0) - (b.totalWan ?? 0), cell: (row: any) => <Box sx={{ fontFamily: "monospace" }}>{(row.totalWan ?? 0).toFixed(2)} GB</Box> },
    { key: "totalAuth", header: "Auth", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => (a.totalAuth ?? 0) - (b.totalAuth ?? 0), cell: (row: any) => <Box sx={{ fontFamily: "monospace", color: "#2f2282" }}>{(row.totalAuth ?? 0).toFixed(2)} GB</Box> },
    { key: "totalStatic", header: "Static", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => (a.totalStatic ?? 0) - (b.totalStatic ?? 0), cell: (row: any) => <Box sx={{ fontFamily: "monospace", color: "#3dab97" }}>{(row.totalStatic ?? 0).toFixed(2)} GB</Box> },
    { key: "totalUnauth", header: "UnAuth", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => (a.totalUnauth ?? 0) - (b.totalUnauth ?? 0), cell: (row: any) => <Box sx={{ fontFamily: "monospace", color: "#d9534f" }}>{(row.totalUnauth ?? 0).toFixed(2)} GB</Box> },
    { key: "avgUsers", header: "Avg Users", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => (a.avgUsers ?? 0) - (b.avgUsers ?? 0), cell: (row: any) => <Box sx={{ fontFamily: "monospace" }}>{(row.avgUsers ?? 0).toFixed(0)}</Box> },
    { key: "avgAuthPerUser", header: "Avg Auth/User", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => (a.avgAuthPerUser ?? -1) - (b.avgAuthPerUser ?? -1), cell: (row: any) => <Box sx={{ fontFamily: "monospace" }}>{row.avgAuthPerUser != null ? row.avgAuthPerUser.toFixed(2) + " GB" : "N/A"}</Box> },
  ];

  const detailColumns = [
    { key: "label", header: "Date", cell: (row: any) => row.label },
    { key: "wan", header: "WAN (GB)", align: "right" as const, cell: (row: any) => <Box sx={{ fontFamily: "monospace" }}>{row.wan.toFixed(2)}</Box> },
    { key: "auth", header: "Auth (GB)", align: "right" as const, cell: (row: any) => <Box sx={{ fontFamily: "monospace", color: "#2f2282" }}>{row.auth.toFixed(2)}</Box> },
    { key: "staticDevices", header: "Static (GB)", align: "right" as const, cell: (row: any) => <Box sx={{ fontFamily: "monospace", color: "#3dab97" }}>{row.staticDevices.toFixed(2)}</Box> },
    { key: "unauth", header: "UnAuth (GB)", align: "right" as const, cell: (row: any) => <Box sx={{ fontFamily: "monospace", color: "#d9534f" }}>{row.unauth.toFixed(2)}</Box> },
    { key: "captiveUsers", header: "Users", align: "right" as const, cell: (row: any) => <Box sx={{ fontFamily: "monospace" }}>{row.captiveUsers}</Box> },
    { key: "authPerUser", header: "Auth/User", align: "right" as const, cell: (row: any) => <Box sx={{ fontFamily: "monospace" }}>{row.authPerUser.toFixed(2)}</Box> },
  ];

  const breakdownOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: "column", height: 300 },
    xAxis: { categories: vesselData.map(d => d.label) },
    yAxis: { title: { text: "GB" } },
    plotOptions: { column: { stacking: "normal" } },
    legend: { enabled: true },
    series: [
      { name: "Auth", data: vesselData.map(d => d.auth), color: "#2f2282" },
      { name: "Static", data: vesselData.map(d => d.staticDevices), color: "#3dab97" },
      { name: "UnAuth", data: vesselData.map(d => d.unauth), color: "#d9534f" },
    ],
  };

  const trendOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: "line", height: 300 },
    xAxis: { categories: vesselData.map(d => d.label) },
    yAxis: { title: { text: "GB" } },
    legend: { enabled: false },
    series: [{ name: "Auth/User", data: vesselData.map(d => d.authPerUser), color: "#5e3f9e" }],
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Ship Analysis</Typography>
          <Typography variant="body2" color="text.secondary">Deep dive into individual vessel performance</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {selectedVessel && (
            <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={onBackToFleet}>
              Back to Fleet
            </Button>
          )}
          <Autocomplete
            size="small"
            sx={{ width: 260 }}
            options={siteAggregates.map(s => s.site)}
            value={selectedVessel}
            onChange={(_, v) => onSelectVessel(v)}
            renderInput={(params) => <TextField {...params} placeholder="Search vessel..." />}
          />
        </Box>
      </Box>

      {!selectedVessel ? (
        <SortableTable
          data={siteAggregates}
          columns={aggregateColumns}
          onRowClick={(row: any) => onSelectVessel(row.site)}
          defaultSortKey="totalWan"
          defaultSortOrder="desc"
        />
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <StatCard highlight title="6-Day Total WAN" value={`${Math.round(vesselAgg?.totalWan || 0).toLocaleString()} GB`} icon={<WifiIcon />} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard title="Avg Daily WAN" value={`${Math.round((vesselAgg?.totalWan || 0) / (vesselAgg?.days || 1)).toLocaleString()} GB`} icon={<ShowChartIcon />} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard title="Avg Captive Users" value={(vesselAgg?.avgUsers || 0).toFixed(0)} icon={<PeopleIcon />} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard title="Avg Auth / User" value={`${(vesselAgg?.avgAuthPerUser || 0).toFixed(2)} GB`} icon={<SecurityIcon />} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>Daily Usage Breakdown</Typography>
                  <HighchartsReact highcharts={Highcharts} options={breakdownOptions} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>Auth per User Trend</Typography>
                  <HighchartsReact highcharts={Highcharts} options={trendOptions} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <SortableTable data={vesselData} columns={detailColumns} />
        </Box>
      )}
    </Box>
  );
}
